import {
  CategoryThematicHeader,
  DefaultThematicHeader,
  HighlightThematicHeader,
  Homepage,
  ThematicHeaderType,
} from 'features/home/types'
import { adaptHomepageNatifModules } from 'libs/contentful/adapters/adaptHomepageModules'
import { buildImageUrl } from 'libs/contentful/adapters/helpers/buildImageUrl'
import {
  HomepageNatifEntry,
  isClassicThematicHeader,
  isThematicCategoryInfo,
  isThematicHighlightInfo,
} from 'libs/contentful/types'
import { buildContentfulIllustrationUrl } from 'shared/illustrations/buildContentfulIllustrationUrl'

const adaptThematicHeader = (homepageEntry: HomepageNatifEntry) => {
  const thematicHeader = homepageEntry.fields.thematicHeader

  if (isThematicHighlightInfo(thematicHeader)) {
    const thematicHeaderFields = thematicHeader.fields
    // if a mandatory module is unpublished/deleted, we can't handle the header, so we return the default one
    if (thematicHeaderFields?.image.fields === undefined) return

    const highlightThematicHeader: HighlightThematicHeader = {
      type: ThematicHeaderType.Highlight,
      title: thematicHeaderFields.displayedTitle,
      subtitle: thematicHeaderFields.displayedSubtitle,
      imageUrl: buildImageUrl(thematicHeaderFields.image.fields.file.url),
      beginningDate: new Date(thematicHeaderFields.beginningDatetime),
      endingDate: new Date(thematicHeaderFields.endingDatetime),
      introductionTitle: thematicHeaderFields.introductionTitle,
      introductionParagraph: thematicHeaderFields.introductionParagraph,
    }
    return highlightThematicHeader
  }

  if (isThematicCategoryInfo(thematicHeader)) {
    const thematicHeaderFields = thematicHeader.fields
    if (thematicHeaderFields === undefined) return

    const legacyImageUrl = buildImageUrl(thematicHeaderFields.image?.fields?.file.url)

    const categoryThematicHeader: CategoryThematicHeader = {
      type: ThematicHeaderType.Category,
      title: thematicHeaderFields.displayedTitle,
      titleParts: thematicHeaderFields.titleParts,
      subtitle: thematicHeaderFields.displayedSubtitle,
      imageUrl: thematicHeaderFields.illustrationFilename
        ? buildContentfulIllustrationUrl(thematicHeaderFields.illustrationFilename)
        : legacyImageUrl,
      color: thematicHeaderFields.color,
    }
    return categoryThematicHeader
  }

  if (isClassicThematicHeader(thematicHeader)) {
    const thematicHeaderFields = thematicHeader.fields
    // if a mandatory module is unpublished/deleted, we can't handle the header, so we return the default one
    if (thematicHeaderFields === undefined) return

    const classicHeader: DefaultThematicHeader = {
      type: ThematicHeaderType.Default,
      title: thematicHeaderFields.displayedTitle,
      subtitle: thematicHeaderFields.displayedSubtitle,
    }
    return classicHeader
  }
  return
}

export const adaptHomepageEntry = (homepageNatifEntry?: HomepageNatifEntry): Homepage => {
  if (!homepageNatifEntry)
    return {
      tags: [],
      id: '',
      modules: [],
      thematicHeader: undefined,
    }
  return {
    tags: homepageNatifEntry.metadata.tags.map((tag) => tag.sys.id),
    id: homepageNatifEntry.sys.id,
    modules: adaptHomepageNatifModules(homepageNatifEntry.fields.modules),
    thematicHeader: adaptThematicHeader(homepageNatifEntry),
  }
}
